import axios from "axios";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import "./profile.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { FaTrashAlt, FaInfoCircle, FaEye, FaEyeSlash } from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { buildImageUrl } from "../../utils/buildImageUrl";

import { notifyUser } from "../../utils/notifyUser";

function Profile({ user }) {
    const [showCurrentPass] = useState(false);
    const [showNewPass, setShowNewPass] = useState(false);
    const [showConfirmPass, setShowConfirmPass] = useState(false);
    const [imageLoadError, setImageLoadError] = useState(false);
    const [, setIsAuthenticated] = useState(false);
    const userId = user?.id;
    const [activeSection, setActiveSection] = useState("generalInfo");
    const [isEditing, setIsEditing] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [currentEmail, setCurrentEmail] = useState("");
    const [currentPassword, setCurrentPassword] = useState("");
    const [preferences, setPreferences] = useState({
        messages: { email: false, sms: false },
        login: { email: false, sms: false },
        others: { email: false, sms: false },
        frequency: "",
    });
    const [formData, setFormData] = useState({
        first_name: "",
        last_name: "",
        email: "",
        phone_number: "",
        company_name: "",
        dba: "",
        profileImage: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zipCode: "",
        role: "",
        issuerId: "",
    });
    const [initialFormData, setInitialFormData] = useState(null);

    // Check authentication status
    useEffect(() => {
        const checkAuth = async () => {
            try {
                // Make an auth check request
                const res = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
                    {
                        withCredentials: true,
                    },
                );
                if (res.status === 200) {
                    setIsAuthenticated(true);
                }
            } catch {
                setIsAuthenticated(false);
                notifyUser(null, "error", "Authentication issue - please login again", "toast", null, null, null, null, null, false, false, "top-center");
                // Redirect to login page after a short delay
                setTimeout(() => {
                    window.location.href = "/login";
                }, 3000);
            }
        };

        checkAuth();
    }, [user]);

    useEffect(() => {
        axios
            .get(
                `${process.env.REACT_APP_BACKEND_URL}/api/profile?user_id=${userId}`,
                {
                    withCredentials: true,
                },
            )
            .then((res) => {
                const data = res.data;

                // Handle both profileImage and profile_image formats from backend
                const profileImagePath =
                    data.profileImage || data.profile_image || "";

                // If we received an empty profile image but have one in state already, keep the existing one
                const finalImagePath =
                    !profileImagePath && formData.profileImage
                        ? formData.profileImage
                        : profileImagePath;

                const loadedData = {
                    ...data,
                    address1: data.address1 || "",
                    address2: data.address2 || "",
                    city: data.city || "",
                    state: data.state || "",
                    zipCode: data.zipCode || "",
                    role: data.role || "",
                    profileImage: finalImagePath,
                    issuerId: data.issuerId || "",
                };
                setFormData(loadedData);
                setInitialFormData(loadedData);

                setPreferences(
                    data.preferences || {
                        messages: { email: false, sms: false },
                        login: { email: false, sms: false },
                        others: { email: false, sms: false },
                        frequency: "",
                    },
                );
            })
            .catch((error) => {
                notifyUser(null, "error", "Failed to load profile", "toast", null, null, null, null, null, false, false, "top-center");
            });
    }, [userId]);

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        if (type === "file") {
            const file = files[0];
            setFormData((prev) => ({ ...prev, profileImage: file }));
            setPreviewUrl(URL.createObjectURL(file));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    // Handle profile image upload
    const handleProfileImageClick = () => {
        document.getElementById("profile-image-input").click();
    };
    const handleProfileImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setPreviewUrl(URL.createObjectURL(file));
        const formData = new FormData();
        formData.append("profileImage", file);
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/profile/upload-picture?user_id=${userId}`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true,
                },
            );
            console.log("Upload response:", res);

            notifyUser(null, "success", "Profile picture updated!", "toast", null, null, null, null, null, false, false, "top-center");
            // Refresh profile data to get the new image path
            const profileRes = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/profile?user_id=${userId}`,
                { withCredentials: true },
            );

            // Handle both profileImage and profile_image formats from backend
            const profileImagePath =
                profileRes.data.profileImage ||
                profileRes.data.profile_image ||
                "";

            setFormData((prev) => ({
                ...prev,
                profileImage: profileImagePath,
            }));
        } catch {
            notifyUser(null, "error", "Failed to upload profile picture", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const validateFields = () => {
        const requiredFields = [
            "first_name",
            "last_name",
            "email",
            "phone_number",
        ];
        for (let field of requiredFields) {
            if (!formData[field]) {
                notifyUser(null, "warning", `Please fill in the required field: ${field.replace(
                        "_",
                        " ",
                    )}`, "toast", null, null, null, null, null, false, false, "top-center");
                return false;
            }
        }
        return true;
    };
    const handleSave = async () => {
        if (!validateFields()) return;

        // Ensure we're sending the current profile image path in the update
        const updatedFormData = {
            ...formData,
            address1: formData.address1.trim(),
            address2: formData.address2 ? formData.address2.trim() : "",
            city: formData.city.trim(),
            state: formData.state.trim(),
            zipCode: formData.zipCode.trim(),
            profile_image: formData.profileImage,
        };

        try {
            // Store current profile image path before updating
            const currentProfileImage = formData.profileImage;

            await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/profile?user_id=${userId}`,
                updatedFormData,
                {
                    headers: { "Content-Type": "application/json" },
                    withCredentials: true,
                },
            );
            notifyUser(null, "success", "Profile updated successfully", "toast", null, null, null, null, null, false, false, "top-center");
            setIsEditing(false);

            // Trigger updateIssuer if entrepreneur and issuer-related fields changed
            if (
                formData.role === "entrepreneur" &&
                formData.issuerId &&
                initialFormData &&
                (formData.first_name !== initialFormData.first_name ||
                    formData.last_name !== initialFormData.last_name ||
                    formData.phone_number !== initialFormData.phone_number ||
                    formData.company_name !== initialFormData.company_name)
            ) {
                try {
                    await axios.post(
                        `${process.env.REACT_APP_BACKEND_URL}/api/issuer/update-issuer`,
                        {
                            issuerId: formData.issuerId,
                            issuerName: formData.company_name,
                            firstName: formData.first_name,
                            lastName: formData.last_name,
                            email: formData.email,
                            phoneNumber: formData.phone_number,
                        },
                        { withCredentials: true },
                    );
                } catch (err) {
                    console.error("Failed to update issuer:", err);
                    // toast.error(
                    //     "Profile saved but failed to update issuer in North Capital",
                    // );
                }
            }

            // Trigger updateIssuerAccount if entrepreneur and account-related fields changed
            if (
                formData.role === "entrepreneur" &&
                formData.issuerId &&
                initialFormData &&
                (formData.company_name !== initialFormData.company_name ||
                    formData.address1 !== initialFormData.address1 ||
                    formData.city !== initialFormData.city ||
                    formData.zipCode !== initialFormData.zipCode ||
                    formData.state !== initialFormData.state)
            ) {
                try {
                    await axios.post(
                        `${process.env.REACT_APP_BACKEND_URL}/api/issuer/update-issuer-account`,
                        {
                            issuerId: formData.issuerId,
                            companyName: formData.company_name,
                            companyState: formData.state,
                            addressline1: formData.address1,
                            addressline2: formData.address2 || "",
                            city: formData.city,
                            zip: formData.zipCode,
                            issuingCountry: "US",
                        },
                        { withCredentials: true },
                    );
                } catch (err) {
                    console.error("Failed to update issuer account:", err);
                    // toast.error(
                    //     "Profile saved but failed to update issuer account in North Capital",
                    // );
                }
            }

            // Clear the preview URL so the image from server is shown
            setPreviewUrl(null);
            // Reset image error state
            setImageLoadError(false);

            // Fetch updated profile immediately to confirm Redis refresh
            const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/profile?user_id=${userId}`,
                {
                    withCredentials: true,
                },
            );

            // Handle both profileImage and profile_image formats from backend
            // If the server returns an empty path, keep using the current one
            let profileImagePath =
                res.data.profileImage || res.data.profile_image || "";
            if (!profileImagePath && currentProfileImage) {
                profileImagePath = currentProfileImage;
            }
            const refreshedData = {
                ...res.data,
                address1: res.data.address1 || "",
                address2: res.data.address2 || "",
                city: res.data.city || "",
                state: res.data.state || "",
                zipCode: res.data.zipCode || "",
                role: res.data.role || "",
                profileImage: profileImagePath,
                issuerId: res.data.issuerId || "",
            };
            setFormData(refreshedData);
            setInitialFormData(refreshedData);
        } catch {
            notifyUser(null, "error", "Failed to update profile", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // Only show profile header and security tab for admin
    if (user?.role === "admin") {
        // Always show security section by default
        return (
            <div className="profile-wrapper relative">
                <div className="profile-header">
                    <input
                        id="profile-image-input"
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={handleProfileImageChange}
                    />
                    {(previewUrl || formData.profileImage) &&
                    !imageLoadError ? (
                        <img
                            src={
                                previewUrl ||
                                buildImageUrl(
                                    process.env.REACT_APP_BACKEND_URL,
                                    formData.profileImage,
                                )
                            }
                            alt="Profile"
                            className="profile-pic"
                            style={{ cursor: "pointer" }}
                            onClick={handleProfileImageClick}
                            title="Click here to upload or update profile picture"
                            onError={() => {
                                setImageLoadError(true);
                            }}
                        />
                    ) : (
                        <i
                            className="fas fa-user-circle text-[70px] text-navy"
                            style={{ cursor: "pointer" }}
                            onClick={handleProfileImageClick}
                            title="Click here to upload or update profile picture"
                        ></i>
                    )}
                    <div className="profile-details">
                        <h2>
                            {formData.first_name} {formData.last_name} |{" "}
                            {formData.email}
                        </h2>
                    </div>
                </div>
                <div className="flex justify-center gap-4 mt-6">
                    <button
                        className={`w-40 py-2 bg-navy text-white font-bold rounded`}
                        style={{ pointerEvents: "none" }}
                    >
                        Security
                    </button>
                </div>
                {/* Always show security section for admin */}
                <div className="change-credentials">
                    <h3>CHANGE YOUR PASSWORD</h3>
                    <div className="credentials-form">
                        <label className="required">Current Email:</label>
                        <input
                            type="text"
                            placeholder="Enter current email"
                            value={currentEmail}
                            onChange={(e) => setCurrentEmail(e.target.value)}
                        />
                        <label className="required">Current Password:</label>
                        <input
                            type={showCurrentPass ? "text" : "password"}
                            placeholder="Enter current password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                        />
                        <button
                            onClick={async () => {
                                if (!currentEmail || !currentPassword) {
                                    notifyUser(null, "warning", "Please enter Email and password first", "toast", null, null, null, null, null, false, false, "top-center");
                                    return;
                                }
                                try {
                                    const res = await axios.post(
                                        `${process.env.REACT_APP_BACKEND_URL}/validate`,
                                        {
                                            email: currentEmail,
                                            password: currentPassword,
                                        },
                                    );
                                    if (res.data.success) {
                                        setShowPasswordModal(true);
                                    } else {
                                        notifyUser(null, "error", "Username or password is incorrect", "toast", null, null, null, null, null, false, false, "top-center");
                                    }
                                } catch {
                                    notifyUser(null, "error", "Username or password is incorrect", "toast", null, null, null, null, null, false, false, "top-center");
                                }
                            }}
                        >
                            CHANGE
                        </button>
                    </div>
                </div>
                {showPasswordModal && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <h3>Update Password</h3>
                            {/* New Password Field */}
                            <div
                                className="form-group"
                                style={{
                                    position: "relative",
                                    textAlign: "left",
                                }}
                            >
                                <div
                                    className="label-with-tooltip"
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <label className="required">
                                        New Password
                                    </label>
                                    <span
                                        className="tooltip-icon-inline"
                                        style={{ marginLeft: "10px" }}
                                    >
                                        <FaInfoCircle />
                                        <span className="tooltip-text">
                                            Password must:
                                            <ul
                                                style={{
                                                    paddingLeft: "18px",
                                                    marginTop: "6px",
                                                }}
                                            >
                                                <li>
                                                    {" "}
                                                    Be at least 8 characters
                                                </li>
                                                <li>
                                                    {" "}
                                                    Include a lowercase letter
                                                    (a–z)
                                                </li>
                                                <li>
                                                    {" "}
                                                    Include an uppercase letter
                                                    (A–Z)
                                                </li>
                                                <li> Include a number (0–9)</li>
                                                <li>
                                                    {" "}
                                                    Include a special character
                                                    (!@#$%^&*)
                                                </li>
                                            </ul>
                                        </span>
                                    </span>
                                </div>
                                <input
                                    type={showNewPass ? "text" : "password"}
                                    placeholder="New Password"
                                    value={newPassword}
                                    onChange={(e) =>
                                        setNewPassword(e.target.value)
                                    }
                                />
                                <span
                                    className="toggle-eye"
                                    onClick={() =>
                                        setShowNewPass((prev) => !prev)
                                    }
                                    style={{
                                        position: "absolute",
                                        right: 10,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        cursor: "pointer",
                                        color: "#666",
                                    }}
                                >
                                    {showNewPass ? <FaEyeSlash /> : <FaEye />}
                                </span>
                            </div>
                            {/* Confirm Password Field */}
                            <div
                                className="form-group"
                                style={{
                                    position: "relative",
                                    textAlign: "left",
                                }}
                            >
                                <label className="required">
                                    Confirm Password
                                </label>
                                <input
                                    type={showConfirmPass ? "text" : "password"}
                                    placeholder="Confirm Password"
                                    value={confirmPassword}
                                    onChange={(e) =>
                                        setConfirmPassword(e.target.value)
                                    }
                                />
                                <span
                                    className="toggle-eye"
                                    onClick={() =>
                                        setShowConfirmPass((prev) => !prev)
                                    }
                                    style={{
                                        position: "absolute",
                                        right: 10,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        cursor: "pointer",
                                        color: "#666",
                                    }}
                                >
                                    {showConfirmPass ? (
                                        <FaEyeSlash />
                                    ) : (
                                        <FaEye />
                                    )}
                                </span>
                            </div>
                            {/* Buttons */}
                            <div className="modal-actions">
                                <button
                                    onClick={async () => {
                                        if (!newPassword || !confirmPassword) {
                                            notifyUser(null, "warning", "Please fill all password fields", "toast", null, null, null, null, null, false, false, "top-center");
                                            return;
                                        }
                                        if (newPassword !== confirmPassword) {
                                            notifyUser(null, "error", "Passwords do not match", "toast", null, null, null, null, null, false, false, "top-center");
                                            return;
                                        }
                                        try {
                                            await axios.put(
                                                `${process.env.REACT_APP_BACKEND_URL}/password`,
                                                {
                                                    user_id: userId,
                                                    password: newPassword,
                                                },
                                            );
                                            notifyUser(null, "success", "Password updated!", "toast", null, null, null, null, null, false, false, "top-center");
                                            setShowPasswordModal(false);
                                            setNewPassword("");
                                            setConfirmPassword("");
                                        } catch {
                                            notifyUser(null, "error", "Failed to update password", "toast", null, null, null, null, null, false, false, "top-center");
                                        }
                                    }}
                                >
                                    Save
                                </button>
                                <button
                                    onClick={() => setShowPasswordModal(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="profile-wrapper relative">
            <div className="profile-header">
                <input
                    id="profile-image-input"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleProfileImageChange}
                />
                {(previewUrl || formData.profileImage) && !imageLoadError ? (
                    <img
                        src={
                            previewUrl ||
                            buildImageUrl(
                                process.env.REACT_APP_BACKEND_URL,
                                formData.profileImage,
                            )
                        }
                        alt="Profile"
                        className="profile-pic"
                        style={{ cursor: "pointer" }}
                        onClick={handleProfileImageClick}
                        title="Click here to upload or update profile picture"
                        onError={() => {
                            setImageLoadError(true);
                        }}
                    />
                ) : (
                    <i
                        className="fas fa-user-circle text-[70px] text-navy"
                        style={{ cursor: "pointer" }}
                        onClick={handleProfileImageClick}
                        title="Click here to upload or update profile picture"
                    ></i>
                )}
                <div className="profile-details">
                    <h2>
                        {formData.first_name} {formData.last_name}
                    </h2>
                    <p>
                        {formData.role === "entrepreneur" &&
                            `${formData.company_name} | `}
                        {formData.email} | {formData.address1}
                    </p>
                </div>
            </div>
            <div className="flex justify-center gap-4 mt-6">
                <button
                    onClick={() => setActiveSection("generalInfo")}
                    className={`w-40 py-2 ${
                        activeSection === "generalInfo"
                            ? "bg-navy text-white font-bold"
                            : "bg-[#C2D7E2] text-navy font-bold"
                    } rounded`}
                >
                    General Info
                </button>
                <button
                    onClick={() => setActiveSection("password")}
                    className={`w-40 py-2 ${
                        activeSection === "password"
                            ? "bg-navy text-white font-bold"
                            : "bg-[#C2D7E2] text-navy font-bold"
                    } rounded`}
                >
                    Security
                </button>
                <button
                    onClick={() => setActiveSection("settings")}
                    className={`w-40 py-2 ${
                        activeSection === "settings"
                            ? "bg-navy text-white font-bold"
                            : "bg-[#C2D7E2] text-navy font-bold"
                    } rounded`}
                >
                    Settings
                </button>
            </div>

            {activeSection === "generalInfo" && (
                <form
                    className="profile-form"
                    onSubmit={(e) => e.preventDefault()}
                >
                    {(() => {
                        const role = formData.role;
                        const fields = [
                            ["first_name", "First Name", true],
                            ["last_name", "Last Name", true],
                            ["email", "Email Address", true],
                            ["phone_number", "Phone Number", true],
                            ["address1", "Company Address", true],
                            ["city", "Company City", true],
                            ["state", "Company State", true],
                            ["zipCode", "Zip Code", true],
                        ];
                        // Add entrepreneur-only fields
                        if (role === "entrepreneur") {
                            fields.splice(2, 0, [
                                "company_name",
                                "Company Legal Name",
                                true,
                            ]);
                            fields.splice(3, 0, ["dba", "DBA", true]);
                        }
                        // Custom grouping: group city, state, zipCode in one row, rest in pairs
                        const groupedFields = [];
                        let i = 0;
                        while (i < fields.length) {
                            // Find city, state, zipCode indices
                            if (
                                fields[i + 1] &&
                                fields[i + 1][0] === "state" &&
                                fields[i][0] === "city" &&
                                fields[i + 2] &&
                                fields[i + 2][0] === "zipCode"
                            ) {
                                groupedFields.push(fields.slice(i, i + 3));
                                i += 3;
                            } else {
                                groupedFields.push(fields.slice(i, i + 2));
                                i += 2;
                            }
                        }
                        return groupedFields.map((group, idx) => (
                            <div
                                className="form-row"
                                key={idx}
                                style={
                                    group.length === 3
                                        ? { display: "flex", gap: "16px" }
                                        : {}
                                }
                            >
                                {group.map(([field, label, required]) => (
                                    <div
                                        className="form-group"
                                        key={field}
                                        style={
                                            group.length === 3
                                                ? { flex: 1 }
                                                : {}
                                        }
                                    >
                                        <label
                                            className={
                                                required ? "required" : ""
                                            }
                                        >
                                            {label}
                                        </label>
                                        <input
                                            name={field}
                                            value={formData[field] || ""}
                                            onChange={handleChange}
                                            disabled={
                                                !isEditing || field === "email"
                                            }
                                        />
                                    </div>
                                ))}
                            </div>
                        ));
                    })()}
                    <div className="form-submit">
                        {!isEditing ? (
                            <button
                                type="button"
                                className="aqi-primary-action"
                                onClick={() => setIsEditing(true)}
                            >
                                EDIT
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="aqi-primary-action"
                                onClick={handleSave}
                            >
                                SAVE
                            </button>
                        )}
                    </div>
                </form>
            )}

            {activeSection === "password" && (
                <div className="change-credentials">
                    <h3>CHANGE YOUR PASSWORD</h3>
                    <div className="credentials-form">
                        <label className="required">Current Email:</label>
                        <input
                            type="text"
                            placeholder="Enter current email"
                            value={currentEmail}
                            onChange={(e) => setCurrentEmail(e.target.value)}
                        />

                        <label className="required">Current Password:</label>
                        <input
                            type={showCurrentPass ? "text" : "password"}
                            placeholder="Enter current password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                        />

                        <button
                            onClick={async () => {
                                if (!currentEmail || !currentPassword) {
                                    notifyUser(null, "warning", "Please enter Email and password first", "toast", null, null, null, null, null, false, false, "top-center");
                                    return;
                                }

                                try {
                                    const res = await axios.post(
                                        `${process.env.REACT_APP_BACKEND_URL}/validate`,
                                        {
                                            email: currentEmail,
                                            password: currentPassword,
                                        },
                                        {
                                            withCredentials: true,
                                        },
                                    );

                                    if (res.data.success) {
                                        setShowPasswordModal(true);
                                    } else {
                                        notifyUser(null, "error", "Username or password is incorrect", "toast", null, null, null, null, null, false, false, "top-center");
                                    }
                                } catch {
                                    notifyUser(null, "error", "Username or password is incorrect", "toast", null, null, null, null, null, false, false, "top-center");
                                }
                            }}
                        >
                            CHANGE
                        </button>
                    </div>
                </div>
            )}

            {/* // Notification Preferences hidden via CSS (profile.css).
                // To unhide: open frontend/src/components/Account/profile.css and remove or comment out the rules for
                // .notification-box-row, .frequency-group, and .settings-save-btn
            */}
            {activeSection === "settings" && (
                <div className="settings-container">
                    <div
                        className="settings-header"
                        style={{ marginBottom: "24px" }}
                    >
                        <span className="settings-heading">
                            Notification Preferences
                        </span>
                    </div>

                    <div
                        className="notification-box-row"
                        style={{ flexDirection: "column", gap: "24px" }}
                    >
                        {["messages", "login", "others"].map((category) => (
                            <div
                                key={category}
                                style={{ marginBottom: "24px" }}
                            >
                                <strong
                                    style={{
                                        color: "var(--navy)",
                                        display: "block",
                                        marginBottom: "8px",
                                    }}
                                >
                                    {category.charAt(0).toUpperCase() +
                                        category.slice(1)}{" "}
                                    Preferences
                                </strong>
                                <div className="toggle-row">
                                    {["email", "sms"].map((type) => (
                                        <label
                                            className="toggle-label"
                                            key={type}
                                        >
                                            <span>{type.toUpperCase()}</span>
                                            <label className="switch">
                                                <input
                                                    type="checkbox"
                                                    checked={
                                                        preferences[category]?.[
                                                            type
                                                        ] || false
                                                    }
                                                    onChange={(e) =>
                                                        setPreferences(
                                                            (prev) => ({
                                                                ...prev,
                                                                [category]: {
                                                                    ...prev[
                                                                        category
                                                                    ],
                                                                    [type]: e
                                                                        .target
                                                                        .checked,
                                                                },
                                                            }),
                                                        )
                                                    }
                                                />
                                                <span className="slider round"></span>
                                            </label>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ))}

                        <div
                            className="frequency-group"
                            style={{ maxWidth: 300 }}
                        >
                            <label htmlFor="frequency">
                                Notification Frequency
                            </label>
                            <select
                                id="frequency"
                                className="frequency-select"
                                value={preferences.frequency}
                                onChange={(e) =>
                                    setPreferences((prev) => ({
                                        ...prev,
                                        frequency: e.target.value,
                                    }))
                                }
                            >
                                <option value="">-- Select Frequency --</option>
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                            </select>
                        </div>
                    </div>

                    <button
                        className="settings-save-btn"
                        onClick={async () => {
                            try {
                                await axios.put(
                                    `${process.env.REACT_APP_BACKEND_URL}/api/profile?user_id=${userId}`,
                                    {
                                        ...formData,
                                        preferences,
                                    },
                                    {
                                        withCredentials: true,
                                    },
                                );
                                notifyUser(null, "success", "Preferences saved!", "toast", null, null, null, null, null, false, false, "top-center");
                            } catch {
                                notifyUser(null, "error", "Failed to save preferences", "toast", null, null, null, null, null, false, false, "top-center");
                            }
                        }}
                    >
                        Save Preferences
                    </button>

                    <hr
                        style={{
                            margin: "40px 0 16px 0",
                            border: "none",
                            borderTop: "1px solid #eee",
                        }}
                    />

                    <div className="delete-account-section">
                        <div
                            className="settings-header"
                            style={{
                                cursor: "pointer",
                                alignItems: "center",
                                gap: "10px",
                            }}
                            onClick={() => {
                                Swal.fire({
                                    title: "Are you sure?",
                                    text: "You will not be able to access your account again!",
                                    icon: "warning",
                                    showCancelButton: true,
                                    confirmButtonColor: "var(--navy)",
                                    cancelButtonColor: "#ccc",
                                    confirmButtonText: "Yes, delete it!",
                                }).then(async (result) => {
                                    if (result.isConfirmed) {
                                        try {
                                            await axios.put(
                                                `${process.env.REACT_APP_BACKEND_URL}/api/profile/delete/${userId}`,
                                                {},
                                                {
                                                    withCredentials: true,
                                                },
                                            );
                                            Swal.fire({
                                                icon: "success",
                                                title: "Delete Request Sent!",
                                                text: "Your account delete request has been sent.",
                                                confirmButtonText: "OK",
                                                confirmButtonColor: "var(--navy)",
                                            });

                                            localStorage.clear();
                                            setTimeout(() => {
                                                window.location.href = "/login";
                                            }, 1500);
                                        } catch {
                                            notifyUser(null, "error", "Failed to delete account", "toast", null, null, null, null, null, false, false, "top-center");
                                        }
                                    }
                                });
                            }}
                        >
                            <h3
                                className="settings-heading delete-heading"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                }}
                            >
                                <FaTrashAlt
                                    style={{
                                        color: "#b00020",
                                        fontSize: "22px",
                                    }}
                                />
                                Delete Account
                            </h3>
                        </div>
                        <div
                            className="delete-disclaimer"
                            style={{
                                color: "#b00020",
                                fontSize: "14px",
                                marginTop: "6px",
                                maxWidth: "400px",
                            }}
                        >
                            <span>
                                <strong>Warning:</strong> Deleting your account
                               sends the request to admin and gets deleted only when the admin approves.
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {showPasswordModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>Update Password</h3>

                        {/* New Password Field */}
                        <div
                            className="form-group"
                            style={{ position: "relative", textAlign: "left" }}
                        >
                            <div
                                className="label-with-tooltip"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                }}
                            >
                                <label className="required">New Password</label>
                                <span
                                    className="tooltip-icon-inline"
                                    style={{ marginLeft: "10px" }}
                                >
                                    <FaInfoCircle />
                                    <span className="tooltip-text">
                                        Password must:
                                        <ul
                                            style={{
                                                paddingLeft: "18px",
                                                marginTop: "6px",
                                            }}
                                        >
                                            <li> Be at least 8 characters</li>
                                            <li>
                                                {" "}
                                                Include a lowercase letter (a–z)
                                            </li>
                                            <li>
                                                {" "}
                                                Include an uppercase letter
                                                (A–Z)
                                            </li>
                                            <li> Include a number (0–9)</li>
                                            <li>
                                                {" "}
                                                Include a special character
                                                (!@#$%^&*)
                                            </li>
                                        </ul>
                                    </span>
                                </span>
                            </div>
                            <input
                                type={showNewPass ? "text" : "password"}
                                placeholder="New Password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                            />
                            <span
                                className="toggle-eye"
                                onClick={() => setShowNewPass((prev) => !prev)}
                                style={{
                                    position: "absolute",
                                    right: 10,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    cursor: "pointer",
                                    color: "#666",
                                }}
                            >
                                {showNewPass ? <FaEyeSlash /> : <FaEye />}
                            </span>
                        </div>

                        {/* Confirm Password Field */}
                        <div
                            className="form-group"
                            style={{ position: "relative", textAlign: "left" }}
                        >
                            <label className="required">Confirm Password</label>
                            <input
                                type={showConfirmPass ? "text" : "password"}
                                placeholder="Confirm Password"
                                value={confirmPassword}
                                onChange={(e) =>
                                    setConfirmPassword(e.target.value)
                                }
                            />
                            <span
                                className="toggle-eye"
                                onClick={() =>
                                    setShowConfirmPass((prev) => !prev)
                                }
                                style={{
                                    position: "absolute",
                                    right: 10,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    cursor: "pointer",
                                    color: "#666",
                                }}
                            >
                                {showConfirmPass ? <FaEyeSlash /> : <FaEye />}
                            </span>
                        </div>

                        {/* Buttons */}
                        <div className="modal-actions">
                            <button
                                onClick={async () => {
                                    if (!newPassword || !confirmPassword) {
                                        notifyUser(null, "warning", "Please fill all password fields", "toast", null, null, null, null, null, false, false, "top-center");
                                        return;
                                    }
                                    if (newPassword !== confirmPassword) {
                                        notifyUser(null, "error", "Passwords do not match", "toast", null, null, null, null, null, false, false, "top-center");
                                        return;
                                    }

                                    try {
                                        await axios.put(
                                            `${process.env.REACT_APP_BACKEND_URL}/password`,
                                            {
                                                user_id: userId,
                                                password: newPassword,
                                            },
                                            {
                                                withCredentials: true,
                                            },
                                        );

                                        notifyUser(null, "success", "Password updated!", "toast", null, null, null, null, null, false, false, "top-center");
                                        setShowPasswordModal(false);
                                        setNewPassword("");
                                        setConfirmPassword("");
                                    } catch {
                                        notifyUser(null, "error", "Failed to update password", "toast", null, null, null, null, null, false, false, "top-center");
                                    }
                                }}
                            >
                                Save
                            </button>
                            <button onClick={() => setShowPasswordModal(false)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

Profile.propTypes = {
    user: PropTypes.object,
};

export default Profile;
